// AllCubes - Single InstancedMesh for ALL data points in 3D visualization
import React, { useRef, useMemo, useEffect, useCallback } from 'react';
import { useFrame, useThree } from '@react-three/fiber';
import { Text } from '@react-three/drei';
import * as THREE from 'three';
import {
  DataRow,
  LayoutType,
  PaletteType,
  ThemeType,
  SortDirection,
  SizeDimension,
  ShapeType,
  Position3D,
  BarLabel,
} from '../../types/visualization';
import {
  THEMES,
  OTHERS_THRESHOLD,
  getGradientColor,
  getColumnRange,
  getNumericValue,
  rowMatchesSearch,
} from '../../constants/visualizationThemes';

// Pinned search term type
interface PinnedSearch {
  term: string;
  color: string;
}

interface AllCubesProps {
  rows: DataRow[];
  xAxis: string;
  sortColumn: string;
  sortDirection: SortDirection;
  colorColumn: string;
  colorPalette: PaletteType;
  searchQuery: string;
  pinnedSearches?: PinnedSearch[];
  onCubeClick: (data: DataRow) => void;
  onDoubleClick: (data: DataRow, position: Position3D | null) => void;
  hoveredIndex: number | null;
  onHover: (index: number | null) => void;
  animSpeed?: number;
  layout?: LayoutType;
  scatterXColumn?: string;
  scatterYColumn?: string;
  theme?: ThemeType;
  searchHighlightColor?: string;
  cubeMagnification?: number;
  metallicMode?: boolean;
  shapeType?: ShapeType;
  sizeByColumn?: string | null;
  sizeByDimension?: SizeDimension;
  maxSizeMultiplier?: number;
}

export default function AllCubes({
  rows,
  xAxis,
  sortColumn,
  sortDirection,
  colorColumn,
  colorPalette,
  searchQuery,
  pinnedSearches = [],
  onCubeClick,
  onDoubleClick,
  hoveredIndex,
  onHover,
  animSpeed = 1,
  layout = 'bars',
  scatterXColumn,
  scatterYColumn,
  theme = 'dark',
  searchHighlightColor = '#ff0000',
  cubeMagnification = 1.8,
  metallicMode = false,
  shapeType = 'cube',
  sizeByColumn = null,
  sizeByDimension = 'y',
  maxSizeMultiplier = 3,
}: AllCubesProps) {
  const meshRef = useRef<THREE.InstancedMesh>(null);
  const cubeSize = 0.08;
  const baseGap = 0.01;
  const Y_OFFSET = 0.5;
  const gap = sizeByColumn ? baseGap * maxSizeMultiplier : baseGap;
  const barSpacing = sizeByColumn ? 6 * Math.max(1.5, maxSizeMultiplier / 2) : 6;

  const highlightColorObj = useMemo(() => new THREE.Color(searchHighlightColor), [searchHighlightColor]);

  const startPositions = useRef<Float32Array | null>(null);
  const targetPositions = useRef<Float32Array | null>(null);
  const animationStartTime = useRef(0);
  const baseColorsRef = useRef<THREE.Color[]>([]);
  const lastHoveredRef = useRef<number | null>(null);
  const tempMatrix = useRef(new THREE.Matrix4());
  const tempScale = useRef(new THREE.Vector3(1, 1, 1));

  const colorRange = useMemo(() => getColumnRange(rows, colorColumn), [rows, colorColumn]);

  const sizeRange = useMemo(() => {
    if (!sizeByColumn) return null;
    return getColumnRange(rows, sizeByColumn);
  }, [rows, sizeByColumn]);

  const cubeScales = useMemo(() => {
    if (!sizeByColumn || !sizeRange) return null;

    const scales = new Float32Array(rows.length * 3);
    const { min, max } = sizeRange;
    const range = max - min || 1;

    rows.forEach((row, i) => {
      const val = getNumericValue(row[sizeByColumn]);
      const normalized = (val - min) / range;
      const sizeScale = 0.5 + normalized * (maxSizeMultiplier - 0.5);

      if (shapeType === 'sphere') {
        scales[i * 3] = sizeScale;
        scales[i * 3 + 1] = sizeScale;
        scales[i * 3 + 2] = sizeScale;
      } else if (shapeType === 'pyramid') {
        scales[i * 3] = 1;
        scales[i * 3 + 1] = sizeScale;
        scales[i * 3 + 2] = 1;
      } else {
        scales[i * 3] = sizeByDimension === 'x' ? sizeScale : 1;
        scales[i * 3 + 1] = sizeByDimension === 'y' ? sizeScale : 1;
        scales[i * 3 + 2] = sizeByDimension === 'z' ? sizeScale : 1;
      }
    });

    return scales;
  }, [rows, sizeByColumn, sizeRange, sizeByDimension, maxSizeMultiplier, shapeType]);

  const layoutData = useMemo(() => {
    if (rows.length === 0) return { positions: new Float32Array(0), colors: [] as THREE.Color[], barLabels: [] as BarLabel[], layout };

    const groups: Record<string, { row: DataRow; globalIdx: number }[]> = {};
    const groupOrder: string[] = [];

    rows.forEach((row, idx) => {
      const key = String(row[xAxis] || 'Unknown');
      if (!groups[key]) {
        groups[key] = [];
        groupOrder.push(key);
      }
      groups[key].push({ row, globalIdx: idx });
    });

    groupOrder.sort((a, b) => groups[b].length - groups[a].length);

    const totalItems = rows.length;
    const threshold = Math.max(1, Math.floor(totalItems * OTHERS_THRESHOLD));

    const finalGroups: Record<string, { row: DataRow; globalIdx: number }[]> = {};
    const finalOrder: string[] = [];
    let othersGroup: { row: DataRow; globalIdx: number }[] = [];

    groupOrder.forEach(key => {
      if (groups[key].length >= threshold) {
        finalGroups[key] = groups[key];
        finalOrder.push(key);
      } else {
        othersGroup = othersGroup.concat(groups[key]);
      }
    });

    if (othersGroup.length > 0) {
      finalGroups['Others'] = othersGroup;
      finalOrder.push('Others');
    }

    const displayGroups = finalGroups;
    const displayOrder = finalOrder;

    const positions = new Float32Array(rows.length * 3);
    const colors: THREE.Color[] = new Array(rows.length);

    const sortGroup = (group: { row: DataRow; globalIdx: number }[]) => {
      const sortedGroup = [...group];
      if (sortColumn !== 'originalIndex') {
        sortedGroup.sort((a, b) => {
          const valA = getNumericValue(a.row[sortColumn]);
          const valB = getNumericValue(b.row[sortColumn]);
          return sortDirection === 'asc' ? valA - valB : valB - valA;
        });
      } else if (sortDirection === 'desc') {
        sortedGroup.reverse();
      }
      return sortedGroup;
    };

    const calcColor = (item: { row: DataRow; globalIdx: number }, groupIdx: number | null = null, totalGroups = 1) => {
      if (colorColumn === xAxis && groupIdx !== null && totalGroups > 1) {
        const offset = 0.15;
        const range = 0.85;
        const normalized = offset + (groupIdx / Math.max(1, totalGroups - 1)) * range;
        return getGradientColor(colorPalette, Math.min(1, normalized));
      }
      const val = getNumericValue(item.row[colorColumn]);
      const normalized = colorRange.max !== colorRange.min
        ? (val - colorRange.min) / (colorRange.max - colorRange.min)
        : 0.5;
      return getGradientColor(colorPalette, normalized);
    };

    if (layout === 'scatter') {
      const barLabels: BarLabel[] = [];
      let xMin = Infinity, xMax = -Infinity, yMin = Infinity, yMax = -Infinity;
      for (let i = 0; i < rows.length; i++) {
        const xv = getNumericValue(rows[i][scatterXColumn || xAxis]);
        const yv = getNumericValue(rows[i][scatterYColumn || colorColumn]);
        if (xv < xMin) xMin = xv;
        if (xv > xMax) xMax = xv;
        if (yv < yMin) yMin = yv;
        if (yv > yMax) yMax = yv;
      }

      const scaleX = xMax !== xMin ? 20 / (xMax - xMin) : 1;
      const scaleY = yMax !== yMin ? 15 / (yMax - yMin) : 1;

      rows.forEach((row, idx) => {
        const xVal = getNumericValue(row[scatterXColumn || xAxis]);
        const yVal = getNumericValue(row[scatterYColumn || colorColumn]);
        const x = (xVal - xMin) * scaleX - 10;
        const y = (yVal - yMin) * scaleY + cubeSize / 2;
        const z = (Math.random() - 0.5) * 2;

        positions[idx * 3] = x;
        positions[idx * 3 + 1] = y + Y_OFFSET;
        positions[idx * 3 + 2] = z;
        colors[idx] = calcColor({ row, globalIdx: idx });
      });

      barLabels.push({ label: scatterXColumn || xAxis, x: 0, y: -2, z: 0, rotation: 0 });
      return { positions, colors, barLabels, layout };

    } else if (layout === 'donut' || layout === 'pie') {
      const innerRadius = layout === 'pie' ? 0.3 : 3;
      const ringSpacing = cubeSize + gap;
      const numGroups = displayOrder.length;
      const segmentGap = 0.02;
      const totalGap = segmentGap * numGroups;
      const availableAngle = Math.PI * 2 - totalGap;
      let currentAngle = -Math.PI / 2;
      const barLabels: BarLabel[] = [];

      displayOrder.forEach((groupKey, groupIdx) => {
        const group = displayGroups[groupKey];
        const sortedGroup = sortGroup(group);
        const segmentAngle = (group.length / totalItems) * availableAngle;
        const startAngle = currentAngle;
        const endAngle = currentAngle + segmentAngle;
        const midAngle = (startAngle + endAngle) / 2;

        const cubesPerRing: number[] = [];
        let ring = 0;
        let placedCount = 0;

        while (placedCount < sortedGroup.length) {
          const radius = innerRadius + ring * ringSpacing;
          const circumference = radius * segmentAngle;
          const maxInRing = Math.max(1, Math.floor(circumference / (cubeSize + gap)));
          cubesPerRing.push(Math.min(maxInRing, sortedGroup.length - placedCount));
          placedCount += cubesPerRing[ring];
          ring++;
        }

        const outerRadius = innerRadius + (cubesPerRing.length - 1) * ringSpacing + ringSpacing * 0.8;
        const labelRadius = outerRadius;
        const normalizedAngle = ((midAngle % (2 * Math.PI)) + 2 * Math.PI) % (2 * Math.PI);
        const isBottomHalf = normalizedAngle > Math.PI / 2 && normalizedAngle < (3 * Math.PI / 2);

        barLabels.push({
          label: groupKey.length > 100 ? groupKey.substring(0, 97) + '...' : groupKey,
          x: Math.cos(midAngle) * labelRadius,
          y: 0,
          z: Math.sin(midAngle) * labelRadius,
          angle: midAngle,
          isBottomHalf,
        });

        let cubeIdx = 0;
        cubesPerRing.forEach((countInRing, ringIdx) => {
          const radius = innerRadius + ringIdx * ringSpacing;
          const angleStep = segmentAngle / countInRing;

          for (let i = 0; i < countInRing && cubeIdx < sortedGroup.length; i++) {
            const item = sortedGroup[cubeIdx];
            const angle = startAngle + angleStep * (i + 0.5);
            const x = Math.cos(angle) * radius;
            const z = Math.sin(angle) * radius;
            const y = cubeSize / 2;
            const idx = item.globalIdx;
            positions[idx * 3] = x;
            positions[idx * 3 + 1] = y + Y_OFFSET;
            positions[idx * 3 + 2] = z;
            colors[idx] = calcColor(item, groupIdx, numGroups);
            cubeIdx++;
          }
        });

        currentAngle = endAngle + segmentGap;
      });

      return { positions, colors, barLabels, layout };

    } else if (layout === 'line') {
      const barLabels: BarLabel[] = [];
      const groupWidth = 6;
      const startX = -((displayOrder.length - 1) * groupWidth) / 2;
      const numGroups = displayOrder.length;

      displayOrder.forEach((groupKey, groupIdx) => {
        const group = displayGroups[groupKey];
        const sortedGroup = sortGroup(group);
        const groupX = startX + groupIdx * groupWidth;

        barLabels.push({
          label: groupKey.length > 100 ? groupKey.substring(0, 97) + '...' : groupKey,
          x: groupX,
          y: -1,
          z: 0,
          rotation: 0,
        });

        const cubesPerLayer = Math.ceil(Math.sqrt(group.length / 10));
        const cubesPerDepth = Math.ceil(group.length / cubesPerLayer);

        sortedGroup.forEach((item, idx) => {
          const layerIdx = Math.floor(idx / cubesPerDepth);
          const depthIdx = idx % cubesPerDepth;
          const y = (idx / sortedGroup.length) * 15 + cubeSize / 2;
          const x = groupX + (layerIdx - (cubesPerLayer - 1) / 2) * (cubeSize + gap);
          const z = (depthIdx - (cubesPerDepth - 1) / 2) * (cubeSize + gap);
          const globalIdx = item.globalIdx;
          positions[globalIdx * 3] = x;
          positions[globalIdx * 3 + 1] = y + Y_OFFSET;
          positions[globalIdx * 3 + 2] = z;
          colors[globalIdx] = calcColor(item, groupIdx, numGroups);
        });
      });

      return { positions, colors, barLabels, layout };

    } else if (layout === 'force') {
      const barLabels: BarLabel[] = [];
      const numGroups = displayOrder.length;
      const goldenAngle = Math.PI * (3 - Math.sqrt(5));
      const clusterCenters = displayOrder.map((_, i) => {
        const y = 1 - (i / (numGroups - 1 || 1)) * 2;
        const radius = Math.sqrt(1 - y * y) * 6;
        const theta = i * goldenAngle;
        return { x: Math.cos(theta) * radius, y: y * 4, z: Math.sin(theta) * radius };
      });

      displayOrder.forEach((groupKey, groupIdx) => {
        const group = displayGroups[groupKey];
        const sortedGroup = sortGroup(group);
        const center = clusterCenters[groupIdx];

        barLabels.push({
          label: groupKey.length > 100 ? groupKey.substring(0, 97) + '...' : groupKey,
          x: center.x,
          y: center.y + 1.5,
          z: center.z,
        });

        const clusterRadius = Math.min(2.5, Math.pow(group.length, 0.33) * 0.35);

        sortedGroup.forEach((item, idx) => {
          const phi = Math.acos(1 - 2 * (idx + 0.5) / sortedGroup.length);
          const theta = Math.PI * (1 + Math.sqrt(5)) * idx;
          const r = clusterRadius * Math.pow(idx / sortedGroup.length, 0.33);
          const x = center.x + r * Math.sin(phi) * Math.cos(theta);
          const y = center.y + r * Math.cos(phi);
          const z = center.z + r * Math.sin(phi) * Math.sin(theta);
          const globalIdx = item.globalIdx;
          positions[globalIdx * 3] = x;
          positions[globalIdx * 3 + 1] = y + Y_OFFSET;
          positions[globalIdx * 3 + 2] = z;
          colors[globalIdx] = calcColor(item, groupIdx, numGroups);
        });
      });

      return { positions, colors, barLabels, layout };

    } else {
      // BARS LAYOUT (default)
      const numBars = displayOrder.length;
      const startZ = -((numBars - 1) * barSpacing) / 2;
      const barPositions = displayOrder.map((key, i) => ({
        label: key.length > 100 ? key.substring(0, 97) + '...' : key,
        z: startZ + i * barSpacing,
      }));

      displayOrder.forEach((groupKey, barIndex) => {
        const group = displayGroups[groupKey];
        const barZ = barPositions[barIndex].z;
        const sortedGroup = sortGroup(group);
        const cubesPerRow = Math.min(200, Math.ceil(Math.sqrt(group.length)));

        sortedGroup.forEach((item, gridIdx) => {
          const layer = Math.floor(gridIdx / cubesPerRow);
          const xIdx = gridIdx % cubesPerRow;
          const x = (xIdx - (cubesPerRow - 1) / 2) * (cubeSize + gap);
          const y = layer * (cubeSize + gap) + cubeSize / 2;
          const idx = item.globalIdx;
          positions[idx * 3] = x;
          positions[idx * 3 + 1] = y + Y_OFFSET;
          positions[idx * 3 + 2] = barZ;
          colors[idx] = calcColor(item, barIndex, numBars);
        });
      });

      return { positions, colors, barLabels: barPositions as BarLabel[], layout };
    }
  }, [rows, xAxis, sortColumn, sortDirection, colorColumn, colorRange, colorPalette, cubeSize, gap, barSpacing, layout, scatterXColumn, scatterYColumn]);

  // Matches for current search query (uses searchHighlightColor)
  const searchMatches = useMemo(() => {
    if (!searchQuery) return new Set<number>();
    const matches = new Set<number>();
    rows.forEach((row, idx) => {
      if (rowMatchesSearch(row, searchQuery)) {
        matches.add(idx);
      }
    });
    return matches;
  }, [rows, searchQuery]);

  // Matches for pinned searches (each with its own color)
  const pinnedMatches = useMemo(() => {
    if (!pinnedSearches || pinnedSearches.length === 0) return new Map<number, string>();
    const matches = new Map<number, string>(); // index -> color
    rows.forEach((row, idx) => {
      // Check each pinned search, first match wins
      for (const pinned of pinnedSearches) {
        if (rowMatchesSearch(row, pinned.term)) {
          matches.set(idx, pinned.color);
          break; // Only use first matching pinned search color
        }
      }
    });
    return matches;
  }, [rows, pinnedSearches]);

  useEffect(() => {
    if (layoutData.positions.length === 0) return;
    if (!startPositions.current || startPositions.current.length !== layoutData.positions.length) {
      startPositions.current = new Float32Array(layoutData.positions);
      targetPositions.current = new Float32Array(layoutData.positions);
    } else if (targetPositions.current) {
      startPositions.current.set(targetPositions.current);
      targetPositions.current.set(layoutData.positions);
    }
    animationStartTime.current = performance.now();
  }, [layoutData.positions]);

  useEffect(() => {
    if (!meshRef.current || layoutData.positions.length === 0) return;
    const mesh = meshRef.current;
    const matrix = tempMatrix.current;

    for (let i = 0; i < rows.length; i++) {
      const idx = i * 3;
      matrix.makeTranslation(layoutData.positions[idx], layoutData.positions[idx + 1], layoutData.positions[idx + 2]);
      mesh.setMatrixAt(i, matrix);
    }
    mesh.instanceMatrix.needsUpdate = true;
    mesh.computeBoundingSphere();
  }, [layoutData.positions, rows.length]);

  useEffect(() => {
    baseColorsRef.current = layoutData.colors;
  }, [layoutData.colors]);

  useFrame(() => {
    if (!meshRef.current || !startPositions.current || !targetPositions.current) return;

    const mesh = meshRef.current;
    const matrix = tempMatrix.current;
    const scaleVec = tempScale.current;
    const now = performance.now();
    const globalElapsed = now - animationStartTime.current;
    const waveDelay = 0.02 / animSpeed;
    const animDuration = 400 / animSpeed;
    const startArr = startPositions.current;
    const targetArr = targetPositions.current;

    const prevHovered = lastHoveredRef.current;
    const hoverChanged = prevHovered !== hoveredIndex;
    lastHoveredRef.current = hoveredIndex;

    if (hoverChanged && mesh.geometry.attributes.color) {
      const colorArray = mesh.geometry.attributes.color.array as Float32Array;
      const maxIdx = colorArray.length / 3;

      if (prevHovered !== null && prevHovered < maxIdx && baseColorsRef.current[prevHovered]) {
        const baseColor = baseColorsRef.current[prevHovered];
        const pinnedColor = pinnedMatches.get(prevHovered);
        if (pinnedColor) {
          const pColor = new THREE.Color(pinnedColor);
          colorArray[prevHovered * 3] = pColor.r;
          colorArray[prevHovered * 3 + 1] = pColor.g;
          colorArray[prevHovered * 3 + 2] = pColor.b;
        } else if (searchMatches.has(prevHovered)) {
          colorArray[prevHovered * 3] = highlightColorObj.r;
          colorArray[prevHovered * 3 + 1] = highlightColorObj.g;
          colorArray[prevHovered * 3 + 2] = highlightColorObj.b;
        } else {
          colorArray[prevHovered * 3] = baseColor.r;
          colorArray[prevHovered * 3 + 1] = baseColor.g;
          colorArray[prevHovered * 3 + 2] = baseColor.b;
        }
      }

      if (hoveredIndex !== null && hoveredIndex < maxIdx) {
        colorArray[hoveredIndex * 3] = Math.min(1, highlightColorObj.r + 0.3);
        colorArray[hoveredIndex * 3 + 1] = Math.min(1, highlightColorObj.g + 0.3);
        colorArray[hoveredIndex * 3 + 2] = Math.min(1, highlightColorObj.b + 0.3);
      }

      mesh.geometry.attributes.color.needsUpdate = true;
    }

    const maxInstances = Math.min(rows.length, startArr.length / 3, targetArr.length / 3);
    for (let i = 0; i < maxInstances; i++) {
      const idx = i * 3;
      const cubeStartDelay = i * waveDelay;
      const cubeElapsed = globalElapsed - cubeStartDelay;

      let progress = 0;
      if (cubeElapsed > 0) {
        progress = Math.min(1, cubeElapsed / animDuration);
        progress = 1 - Math.pow(1 - progress, 3);
      }

      const sx = startArr[idx], sy = startArr[idx + 1], sz = startArr[idx + 2];
      const tx = targetArr[idx], ty = targetArr[idx + 1], tz = targetArr[idx + 2];
      const x = sx + (tx - sx) * progress;
      const y = sy + (ty - sy) * progress;
      const z = sz + (tz - sz) * progress;

      const isHovered = i === hoveredIndex;
      const isSearchMatch = searchMatches.has(i);
      const isPinnedMatch = pinnedMatches.has(i);
      const highlightScale = isHovered ? 2.5 : ((isSearchMatch || isPinnedMatch) ? cubeMagnification : 1);

      let scaleX = 1, scaleY = 1, scaleZ = 1;
      if (cubeScales) {
        scaleX = cubeScales[i * 3];
        scaleY = cubeScales[i * 3 + 1];
        scaleZ = cubeScales[i * 3 + 2];
      }

      const baseSize = 0.07;
      let offsetX = 0, offsetY = 0, offsetZ = 0;
      if (cubeScales) {
        if (scaleY > 1) offsetY = (scaleY - 1) * baseSize * 0.5;
        if (scaleX > 1) offsetX = (scaleX - 1) * baseSize * 0.5;
        if (scaleZ > 1) offsetZ = (scaleZ - 1) * baseSize * 0.5;
      }

      matrix.makeTranslation(x + offsetX, y + offsetY, z + offsetZ);
      scaleVec.set(scaleX * highlightScale, scaleY * highlightScale, scaleZ * highlightScale);
      matrix.scale(scaleVec);
      mesh.setMatrixAt(i, matrix);
    }

    mesh.instanceMatrix.needsUpdate = true;
  });

  useEffect(() => {
    if (!meshRef.current || layoutData.colors.length === 0) return;
    const mesh = meshRef.current;
    const colorArray = new Float32Array(rows.length * 3);
    const hlColor = new THREE.Color(searchHighlightColor);

    layoutData.colors.forEach((color, i) => {
      if (color) {
        // Check pinned searches first (they take priority)
        const pinnedColor = pinnedMatches.get(i);
        if (pinnedColor) {
          const pColor = new THREE.Color(pinnedColor);
          colorArray[i * 3] = pColor.r;
          colorArray[i * 3 + 1] = pColor.g;
          colorArray[i * 3 + 2] = pColor.b;
        } else if (searchMatches.has(i)) {
          // Then check current search query
          colorArray[i * 3] = hlColor.r;
          colorArray[i * 3 + 1] = hlColor.g;
          colorArray[i * 3 + 2] = hlColor.b;
        } else {
          // Use base color
          colorArray[i * 3] = color.r;
          colorArray[i * 3 + 1] = color.g;
          colorArray[i * 3 + 2] = color.b;
        }
      }
    });

    const attr = mesh.geometry.getAttribute('color');
    if (attr && (attr as THREE.BufferAttribute).array.length === colorArray.length) {
      (attr as THREE.BufferAttribute).array.set(colorArray);
      attr.needsUpdate = true;
    } else {
      // Size changed or no attribute yet — replace entirely
      mesh.geometry.deleteAttribute('color');
      mesh.geometry.setAttribute('color', new THREE.InstancedBufferAttribute(colorArray, 3));
    }
  }, [layoutData.colors, rows.length, searchMatches, pinnedMatches, searchHighlightColor]);

  // Direct Three.js raycasting — fully bypasses R3F event system
  const { camera, gl } = useThree();
  const manualPointer = useRef(new THREE.Vector2());
  const manualRaycaster = useRef(new THREE.Raycaster());
  const pointerMoved = useRef(false);

  // Track pointer via DOM events on the canvas (R3F pointer may not update without event handlers)
  useEffect(() => {
    const canvas = gl.domElement;
    const onPointerMove = (e: PointerEvent) => {
      const rect = canvas.getBoundingClientRect();
      manualPointer.current.x = ((e.clientX - rect.left) / rect.width) * 2 - 1;
      manualPointer.current.y = -((e.clientY - rect.top) / rect.height) * 2 + 1;
      pointerMoved.current = true;
    };
    canvas.addEventListener('pointermove', onPointerMove);
    return () => canvas.removeEventListener('pointermove', onPointerMove);
  }, [gl]);

  // Manual hover detection via raycasting each frame
  useFrame(() => {
    if (!meshRef.current || !pointerMoved.current) return;
    pointerMoved.current = false;
    const mesh = meshRef.current;

    // Ensure bounding sphere is large enough for raycasting
    if (!mesh.boundingSphere || mesh.boundingSphere.radius < 100) {
      mesh.boundingSphere = new THREE.Sphere(new THREE.Vector3(0, 5, 0), 500);
    }

    manualRaycaster.current.setFromCamera(manualPointer.current, camera);
    const intersects: THREE.Intersection[] = [];
    mesh.raycast(manualRaycaster.current, intersects);

    if (intersects.length > 0 && intersects[0].instanceId !== undefined) {
      onHover(intersects[0].instanceId);
      gl.domElement.style.cursor = 'pointer';
    } else {
      onHover(null);
      gl.domElement.style.cursor = 'auto';
    }
  });

  // Click handler on the canvas DOM element
  useEffect(() => {
    const canvas = gl.domElement;
    const handleCanvasClick = (e: MouseEvent) => {
      if (!meshRef.current) return;
      const mesh = meshRef.current;

      // Ensure bounding sphere
      if (!mesh.boundingSphere || mesh.boundingSphere.radius < 100) {
        mesh.boundingSphere = new THREE.Sphere(new THREE.Vector3(0, 5, 0), 500);
      }

      const rect = canvas.getBoundingClientRect();
      const ptr = new THREE.Vector2(
        ((e.clientX - rect.left) / rect.width) * 2 - 1,
        -((e.clientY - rect.top) / rect.height) * 2 + 1
      );
      const rc = new THREE.Raycaster();
      rc.setFromCamera(ptr, camera);
      const intersects: THREE.Intersection[] = [];
      mesh.raycast(rc, intersects);

      if (intersects.length > 0 && intersects[0].instanceId !== undefined) {
        const idx = intersects[0].instanceId;
        if (rows[idx]) {
          if (e.shiftKey && onDoubleClick && targetPositions.current) {
            const pos = {
              x: targetPositions.current[idx * 3],
              y: targetPositions.current[idx * 3 + 1],
              z: targetPositions.current[idx * 3 + 2],
            };
            onDoubleClick(rows[idx], pos);
          } else {
            onCubeClick(rows[idx]);
          }
        }
      }
    };
    canvas.addEventListener('click', handleCanvasClick);
    return () => canvas.removeEventListener('click', handleCanvasClick);
  }, [gl, camera, rows, onCubeClick, onDoubleClick]);

  if (rows.length === 0) return null;

  const labelColor = theme === 'dark' ? '#ffffff' : '#1d1d1f';
  const outlineColor = theme === 'dark' ? '#000000' : '#ffffff';
  const labelProps = {
    color: labelColor,
    outlineWidth: 0.012,
    outlineColor,
    outlineBlur: 0.008,
    outlineOpacity: 0.6,
    sdfGlyphSize: 128,
  } as const;

  return (
    <group>
      {(layoutData.layout === 'donut' || layoutData.layout === 'pie') ? (
        layoutData.barLabels.map(({ label, x, y, z }, idx) => (
          <Text
            key={`${idx}-${label}`}

            position={[x || 0, (y || 0) + 0.3, z]}
            fontSize={0.25}
            {...labelProps}
            anchorX="center"
            anchorY="middle"
          >
            {label}
          </Text>
        ))
      ) : layoutData.layout === 'scatter' ? (
        layoutData.barLabels.map(({ label, x, y, z }, idx) => (
          <Text
            key={`${idx}-${label}`}

            position={[x || 0, y || 0, z]}
            fontSize={0.4}
            {...labelProps}
            anchorX="center"
            anchorY="top"
          >
            {label}
          </Text>
        ))
      ) : layoutData.layout === 'line' ? (
        layoutData.barLabels.map(({ label, x, y, z }, idx) => (
          <Text
            key={`${idx}-${label}`}

            position={[x || 0, y || 0, z]}
            fontSize={0.3}
            {...labelProps}
            anchorX="center"
            anchorY="top"
          >
            {label}
          </Text>
        ))
      ) : layoutData.layout === 'force' ? (
        layoutData.barLabels.map(({ label, x, y, z }, idx) => (
          <Text
            key={`${idx}-${label}`}

            position={[x || 0, y || 0, z]}
            fontSize={0.3}
            {...labelProps}
            anchorX="center"
            anchorY="bottom"
          >
            {label}
          </Text>
        ))
      ) : (
        layoutData.barLabels.map(({ label, z }, idx) => (
          <Text
            key={`${idx}-${label}`}

            position={[0, -1, z + 0.5]}
            fontSize={0.4}
            {...labelProps}
            anchorX="center"
            anchorY="top"
          >
            {label}
          </Text>
        ))
      )}

      <instancedMesh
        ref={meshRef}
        args={[undefined, undefined, rows.length]}
        frustumCulled={false}
      >
        {shapeType === 'sphere' ? (
          <sphereGeometry args={[0.035, 16, 12]} />
        ) : shapeType === 'pyramid' ? (
          <coneGeometry args={[0.04, 0.07, 4]} />
        ) : (
          <boxGeometry args={[0.07, 0.07, 0.07]} />
        )}
        <meshStandardMaterial
          vertexColors
          emissive={theme === 'dark' ? '#666666' : '#333333'}
          emissiveIntensity={metallicMode ? (theme === 'dark' ? 0.3 : 0.2) : (theme === 'dark' ? 0.5 : 0.3)}
          roughness={metallicMode ? 0.15 : 0.2}
          metalness={metallicMode ? 0.9 : 0.1}
          envMapIntensity={metallicMode ? 1.5 : 1}
        />
      </instancedMesh>
    </group>
  );
}
